import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import { useState } from 'react'

import { useFieldHover } from '../../hooks/useFieldHover'
import { cn } from '../../utils/cn'
import { Button } from './button'
import { Command, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from './command'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

export interface SearchableSelectOption {
  value: string
  label: string
  /** Second line under the label — what tells two similar rows apart. */
  description?: string
  /** Matched by the search but not shown, e.g. a full license number. */
  keywords?: string[]
}

/** Letters and digits only, so "1xp-bdp" finds "1XPBDP" and "abc 123" finds "ABC123". */
function squash(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/** The options matching every word typed, in their original order. */
export function filterOptions(options: SearchableSelectOption[], query: string): SearchableSelectOption[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return options
  return options.filter((option) => {
    const text = [option.label, option.description ?? '', ...(option.keywords ?? [])].join(' ').toLowerCase()
    const squashed = squash(text)
    return words.every((word) => text.includes(word) || squashed.includes(squash(word)))
  })
}

interface SearchableSelectProps {
  options: SearchableSelectOption[]
  value: string | undefined
  onChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  /** Pinned under the matches whatever is typed, e.g. "Truck not on this list". */
  footerOption?: SearchableSelectOption
  id?: string
  disabled?: boolean
  className?: string
  'aria-invalid'?: boolean
}

function OptionItem({
  option,
  selected,
  onSelect,
}: {
  option: SearchableSelectOption
  selected: boolean
  onSelect: (value: string) => void
}) {
  return (
    <CommandItem value={option.value} onSelect={() => onSelect(option.value)} className="cursor-pointer">
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{option.label}</span>
        {option.description && (
          <span className="text-muted-foreground truncate text-xs">{option.description}</span>
        )}
      </div>
      <CheckIcon className={cn('size-4', selected ? 'opacity-100' : 'opacity-0')} />
    </CommandItem>
  )
}

/** A select you can type into — for lists too long to scroll, like a 300-trailer fleet. */
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select',
  searchPlaceholder = 'Search',
  emptyMessage = 'No matches',
  footerOption,
  id,
  disabled,
  className,
  'aria-invalid': ariaInvalid,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const hoverProps = useFieldHover<HTMLButtonElement>()

  const matches = filterOptions(options, query)
  const selected = [...options, ...(footerOption ? [footerOption] : [])].find((o) => o.value === value)

  function toggle(next: boolean) {
    setOpen(next)
    if (!next) setQuery('')
  }

  function select(next: string) {
    onChange(next)
    toggle(false)
  }

  return (
    <Popover open={open} onOpenChange={toggle}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outlineThin"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          disabled={disabled}
          data-slot="searchable-select-trigger"
          {...hoverProps}
          className={cn(
            'border-input hover:data-hovered:not-focus-visible:border-ink-300 hover:bg-surface text-md h-10 w-full justify-between gap-2 px-3 font-normal md:text-sm',
            className
          )}
        >
          <span className={cn('min-w-0 flex-1 truncate text-left', !selected && 'text-ink-300')}>
            {selected?.label ?? placeholder}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-72 p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
          <CommandList>
            {matches.length > 0 ? (
              <CommandGroup>
                {matches.map((option) => (
                  <OptionItem
                    key={option.value}
                    option={option}
                    selected={option.value === value}
                    onSelect={select}
                  />
                ))}
              </CommandGroup>
            ) : (
              <div className="text-muted-foreground py-6 text-center text-sm">{emptyMessage}</div>
            )}
            {footerOption && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <OptionItem
                    option={footerOption}
                    selected={footerOption.value === value}
                    onSelect={select}
                  />
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
